const createMemoizedFunction = (original, dependencies, cache) => function (...args) {
    const currentDeps = dependencies.map((dep) => this[dep]);
    const key = JSON.stringify([...args, ...currentDeps]);
    if (cache.has(key)) {
        return cache.get(key);
    }
    const result = original.apply(this, args);
    cache.set(key, result);
    return result;
};
/**
 * Decorator que memoriza o resultado de um método. Caso os parâmetros de uma nova
 * chamada ao método sejam os mesmos, o método não irá rodar novamente,
 * apenas retornar o mesmo valor já memorizado.
 *
 * @param {string[]} [dependencies] Lista de dependências que o método tem. Caso alguma dessas dependências
 * seja alterada, o método irá rodar novamente.
 *
 * @category Decorator
 *
 * @example
 * ```ts
 * import { Memo } from '@ids/tools';
 *
 * export class ExemploClasse {
 *   private _x: number;
 *   private _y: number;
 *
 *   constructor(x: number, y: number) {
 *     this._x = x;
 *     this._y = y;
 *   }
 *
 *   @Memo(['_x', '_y'])
 *   add(): number {
 *     return this._x + this._y;
 *   }
 *
 *   onClick() {
 *     console.log(this.somar()); // registra a soma de _x e _y
 *     console.log(this.somar()); // registra a soma de _x e _y novamente (resultado é armazenado em cache)
 *     this._x = 5;
 *     console.log(this.somar()); // registra a nova soma de _x e _y (cache é invalidado devido à mudança em _x)
 *   }
 * }
 * ```
 */
export const Memo = (dependencies = []) => (_target, _key, propertyDescriptor) => {
    const cache = new Map();
    if (propertyDescriptor?.value) {
        const original = propertyDescriptor.value;
        propertyDescriptor.value = createMemoizedFunction(original, dependencies, cache);
        return;
    }
    if (propertyDescriptor?.get) {
        const original = propertyDescriptor.get;
        propertyDescriptor.get = createMemoizedFunction(original, dependencies, cache);
        return;
    }
};
